// The cross-tab sync lock, named per LOCAL DATABASE (W11, and since W12 A5 per account store).
// Quil's own file (not canonical); the lock itself is the canonical `syncLock.ts`.
//
// The thing the lock protects is the Dexie store every tab shares. Before W12 that store was always
// `QuilDatabase`, so the lock was always `quil:QuilDatabase:sync`. Now an account has its own store
// (`QuilDatabase@<homeAccountId>`; the adopted pre-W12 store keeps `QuilDatabase`), and the lock is
// `syncLockName('quil', scope)` (`quil:<store>:sync`): two accounts' stores never queue behind each
// other, and two tabs of ONE store still do. The adopted legacy store keeps the name it always had.
//
// `maxHoldMs` is 300 s: a first sign-in can reconcile a whole library under one hold; past it the
// holder's signal aborts and the lock is released, so a hung request can never starve another tab.
import { createSyncLock } from './syncLock'
import type { SyncLock } from './syncLock'
import { syncLockName } from './storageScope'
import type { StoreScope } from './storageScope'
import { BASE } from './scopeKeys'

/** What the lock is named while no scope is bound: the pre-W12 name, unchanged. */
const LEGACY_SCOPE: StoreScope = { accountId: '', storeName: BASE, legacy: true }
const SYNC_LOCK_MAX_HOLD_MS = 300_000

let current: StoreScope = LEGACY_SCOPE
const locks = new Map<string, SyncLock>()

/** The lock name for a store (the legacy store keeps its old name).
 * @internal Exported for its unit tests; production reaches it only from inside this module.
 */
export function lockNameFor(scope: StoreScope): string {
  return syncLockName('quil', scope)
}

/**
 * Point the app's one lock at the store this tab is bound to. Called once, when the store is
 * decided, BEFORE any sync pass can start; `null` goes back to the pre-W12 name.
 */
export function bindCoordination(scope: StoreScope | null): void {
  current = scope ?? LEGACY_SCOPE
}

/** The sync lock of the bound store, created on first use. */
export function appSyncLock(): SyncLock {
  const name = lockNameFor(current)
  let lock = locks.get(name)
  if (!lock) {
    lock = createSyncLock({ name, maxHoldMs: SYNC_LOCK_MAX_HOLD_MS })
    locks.set(name, lock)
  }
  return lock
}
